import { Box, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { PmiButton } from 'shell/components/shared/button';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { CancellationAireFormVM } from 'cancellations/view-models/cancellation-aire';
import { transformerCancellationAireData } from 'cancellations/utils/transform-anpr-cancellation-aire';
import { CancellationAireCitizenshipLossCrumbs } from 'cancellations/constants/cancellation-aire-citizenship-loss-crumbs';
import CancellationAire from 'cancellations/components/cancellation-aire/content';
import { validationSchema } from 'cancellations/constants/cancellation-aire-validation-schema';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import PmiTitle from 'shell/components/shared/title';
import { transformRequestCancellationAireCitizenshipLost } from 'cancellations/utils/transform-request-cancellation-citizenship-lost';

const CancellationAireLossCitizenshipPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CancellationAireFormVM>(
    `${__PERSON_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
            soggettoAIRE: 'S',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => transformerCancellationAireData(response),
    }
  );

  const [responseCancellationLossCitizenship, cancellationLossCitizenship] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/cancellazione-aire/cittadinanza`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseCancellationLossCitizenship.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleSubmit = useCallback(
    (values: CancellationAireFormVM) => {
      const data = transformRequestCancellationAireCitizenshipLost(values);
      (
        cancellationLossCitizenship({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate('/cancellazioni');
      });
    },
    [navigate, cancellationLossCitizenship, setVisibility]
  );

  const handleExit = useCallback(() => {
    navigate('/cancellazioni');
  }, [navigate]);

  //Errors;
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  return (
    <Fragment>
      <Breadcrumbs items={CancellationAireCitizenshipLossCrumbs} />
      <PmiTitle title="Cancellazione AIRE per Perdita Cittadinanza" />
      {anpr.data && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={
            {
              ...((anpr.data as CancellationAireFormVM) || {}),
              cancellationAire: {
                lossCitizenshipDate: null,
                isCitizenshipLoss: true,
                cancellationDate: null,
                newCitizenship: undefined,
                opeLegis: undefined,
                subevent: '',
                typeInitiative: '',
              },
            } as CancellationAireFormVM
          }
          onSubmit={handleSubmit}
          validateOnMount
          validationSchema={validationSchema}
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <StepButton active fullWidth>
                  <StepTypography active>Anagrafe</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      id="submit"
                      fullWidth
                      type="submit"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      id="deny-operation"
                      fullWidth
                      onClick={handleExit}
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Annulla Operazione
                      </Box>
                    </PmiButton>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={10}>
                <Grid container spacing={2} item xs={12}>
                  <Grid item xs={12}>
                    <SearchMessages
                      type={MessageType.Error}
                      title={errorMessage.title || ''}
                      messages={errorMessage.messages || []}
                      visibility={errorMessage.visibility || false}
                      onChangeVisibility={onVisibility}
                    />
                    <SearchMessages
                      type={MessageType.Warning}
                      title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
                      messages={castedSavedData?.warnings || []}
                      visibility={
                        visibility.warning !== undefined
                          ? visibility.warning
                          : (castedSavedData?.warnings || [])?.length > 0
                      }
                      onChangeVisibility={onVisibility}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CancellationAire isAire isCitizenship isLossCitizenship />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

CancellationAireLossCitizenshipPage.displayName =
  'Cancellation Aire Loss Citizenship page component';

export default CancellationAireLossCitizenshipPage;
